import { OFFLINE_ORDER_TTL_HOURS } from '@cuito/codes';

/**
 * 离线接单时限（契约 §20.6、`packages/codes` 的 `OFFLINE_ORDER_TTL_HOURS`）的**客户端投影**。
 *
 * **为什么必须有这个文件**：订单码是自重码，码内带着签发时刻 `issuedAt`；离线时收银端无法回源，
 * 唯一的时限判据只能是「码签发到现在多久」。服务端在 `POST /orders/offline-gate` 那条路上读
 * **同一个符号**（`apps/api/src/common/errors/order-code-gate.ts`），前端这一侧此前一个消费者都没有
 * ⇒ `OFFLINE_ORDER_TTL_HOURS` 实际上是「服务端单边常量」，而收银端一旦在别处写死一个 `24`，
 * 两端的时限就会各自漂移（静态⑯ 判的正是这个：**零第二份 `24` 字面量**）。
 *
 * 时限的唯一事实来源是 `@cuito/codes`，本文件**只做一次单位换算**，不复制那个数字。
 *
 * ⚠️ 本文件里**不许**出现裸 `24`（换算写成 `OFFLINE_ORDER_TTL_HOURS * 60 * 60 * 1000`）。
 */

/** 时限的毫秒形态（由 `OFFLINE_ORDER_TTL_HOURS` 换算，不是第二个事实来源） */
export const OFFLINE_ORDER_TTL_MS = OFFLINE_ORDER_TTL_HOURS * 60 * 60 * 1000;

/**
 * 码是否已超出离线接单时限。
 *
 * 边界取**大于等于**（与 `dataAsOf.isStale` 的严格大于**刻意不同**，别顺手统一）：
 * 陈旧与否是给人看的提示，多显示一秒没有代价；而过期的码是**拒绝接单**的判据，
 * 恰好卡在时限上的那一毫秒必须落在「不许接」这一侧 —— 服务端的闸门同理。
 *
 * `issuedAt` 读不出来（非法字符串）时按**已过期**处理：宁可让店员让顾客重新下单，
 * 也不能拿一张读不出签发时刻的码去收款（那正是 R-02 的形态）。
 */
export function offlineOrderCodeExpired(issuedAt: string | Date, now: Date = new Date()): boolean {
  const issued = issuedAt instanceof Date ? issuedAt : new Date(issuedAt);
  const issuedMs = issued.getTime();
  if (Number.isNaN(issuedMs)) return true;
  return now.getTime() - issuedMs >= OFFLINE_ORDER_TTL_MS;
}

/**
 * 订单码**载荷**里的 `issuedAt` 形态 ⇒ 是否过期（Phase 9 Task 9 的调用点用这一个）。
 *
 * ⚠️ **就地订正一处事实**：本文件此前的注释写「订单码载荷里的 `issuedAt` 就是字符串」——
 * 那是**错的**。`packages/codes/src/order-code.ts` 的 `OrderCodeHeader.issuedAt` 是
 * **10 位 Unix 秒**（`number`，编码器的守卫逐字写着「`issuedAt` 是 10 位 Unix 秒」，
 * 见该文件第 174 行附近的注释与 `assertPlainDigits(input.issuedAt, 'ISSUED_AT')`）。
 * 于是 `offlineOrderCodeExpired(header.issuedAt)` 这个写法**编译不过**
 * （签名收 `string | Date`），而硬塞一个数字进去（`as unknown as string`）会把
 * 1970 年当成签发时刻 ⇒ **每一张码都被判过期**，收银端在断网时根本接不了单。
 *
 * 秒 → 毫秒的换算**只有这一处**（与 `OFFLINE_ORDER_TTL_MS` 同一个文件、同一条时限判据）：
 * 散在页面里做 `* 1000`，就是给「时限」造第二个入口。
 */
export function offlineOrderCodeExpiredAtSeconds(issuedAtSeconds: number, now: Date = new Date()): boolean {
  // 非有限值 / 非正数 ⇒ 读不出签发时刻 ⇒ 按已过期处理（与字符串那条路同一个取舍）
  if (!Number.isFinite(issuedAtSeconds) || issuedAtSeconds <= 0) return true;
  return offlineOrderCodeExpired(new Date(issuedAtSeconds * 1000), now);
}
